'use client';

import React, { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingLogo, { FullPageLoading } from './animated-logo';

type LoadingWrapperProps = {
  children: ReactNode;
  isLoading: boolean;
  message?: string;
  size?: 'small' | 'medium' | 'large' | 'full';
  className?: string;
  showMessage?: boolean;
};

/**
 * A wrapper component for any content that might need a loading state
 * Can be used in various sizes: small, medium, large or full-page
 */
export const LoadingWrapper = ({
  children,
  isLoading,
  message = 'Loading...',
  size = 'medium',
  className = '',
  showMessage = true,
}: LoadingWrapperProps) => {
  if (size === 'full') {
    return (
      <div className={`relative ${className}`}>
        <AnimatePresence mode="wait">
          {isLoading ? (
            <FullPageLoading message={showMessage ? message : undefined} />
          ) : (
            <>{children}</>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Size mappings
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-16 h-16',
    large: 'w-24 h-24',
  };

  const messageClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base',
  };

  return (
    <div className={`relative ${className}`}>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-full w-full bg-slate-900/80 backdrop-blur-sm rounded-lg">
          <motion.div
            className={`${sizeClasses[size]} mb-3`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <LoadingLogo />
          </motion.div>
          
          {showMessage && (
            <motion.p
              className={`text-white/70 ${messageClasses[size]}`}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {message}
            </motion.p>
          )}
        </div>
      ) : (
        <>{children}</>
      )}
    </div>
  );
};

export default LoadingWrapper;